import { useAllVoices } from '@/hooks/useAllVoices';
import { AddAvatarCard } from '@/pages/AdvancedEditor/components/panels/avatars/AvatarSection/AddAvatarCard';
import { UpgradeModal } from '@/pages/AdvancedEditor/components/panels/avatars/AvatarSection/UpgradeModal';
import { PresentersList } from '@/pages/AdvancedEditor/components/panels/avatars/PresentersList';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { PolotnoStoreActivePageKeys, usePolotnoActivePageState, useSetPolotnoActivePageState } from '@/pages/AdvancedEditor/hooks';
import { getAvatarPresenterFromPage } from '@/pages/AdvancedEditor/utils';
import {
    AvatarFilter,
    AvatarFilterDeprecated,
    AvatarTabs,
    useAvatarsFilters,
    useAvatarsSorting,
    useOpenCreateAvatarModal,
    useUploadAvatar,
} from '@/pages/avatar';
import { useAllAvatars } from '@/pages/avatar/hooks/useAllAvatars';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { getDefaultVoice } from '@/services/presenters/getDefaultVoice';
import theme from '@/theme';
import { MixPanelEvent } from '@/utils/types';
import { isNonPremiumUser } from '@/utils/utils';
import { ImageType, Presenter, PresenterUsecase, VideoType } from '@d-id/types';
import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PresentersContainer } from '../PresentersContainer';
import { useAddAvatarToPolotno } from '../utils/useAddAvatarToPolotno';
import AvatarDiscoverabilityCard from './AvatarDiscoverabilityCard';

const UploadTooltipAnchor = styled.div`
    display: flex;

    > div {
        width: 100% !important;
    }
`;

interface Props {
    setEditMode: (editMode: boolean) => void;
}

export const AvatarSection = observer(({ setEditMode }: Props) => {
    const mixpanel = useAnalytics();
    const { store, setPresenterToDelete } = useAdvancedEditorContext();
    const addAvatar = useAddAvatarToPolotno();

    const { data: voices = [] } = useAllVoices();
    const { allAvatars, userAvatars, isLoading: avatarsLoading } = useAllAvatars(PresenterUsecase.Video, 'all');
    const { sortAvatars } = useAvatarsSorting();

    const [filter, setFilter] = useState<AvatarFilter | AvatarFilterDeprecated>(AvatarFilter.All);
    const filteredPresenters = useAvatarsFilters(
        allAvatars.filter(({ status }) => !status || status === 'done'),
        filter
    );
    const sortedPresenters = sortAvatars(filteredPresenters);

    const presentersToShow =
        filter === AvatarFilter.MyAvatars ? sortedPresenters : sortedPresenters.filter(p => p.presenterType !== ImageType.Dreams);

    const setSelectedVoice = useSetPolotnoActivePageState(PolotnoStoreActivePageKeys.ttsSelectedVoice);
    const setSelectedLanguage = useSetPolotnoActivePageState(PolotnoStoreActivePageKeys.ttsSelectedLanguage);
    const [ttsIsSelectedDefault, setTtsIsSelectedDefault] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.ttsIsSelectedDefault);
    const currentAvatar = getAvatarPresenterFromPage(store.activePage);
    const setSelectedPresenter = useSetPolotnoActivePageState(PolotnoStoreActivePageKeys.selectedPresenter);
    const openCreateAvatarModal = useOpenCreateAvatarModal();
    const { pathname } = useLocation();

    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const { user, isAnonymous } = useAuth();
    const userPlan = user?.planGroup ?? user?.productName;

    const avatarsDiscoveryEnabled = useFlag('avatars_discovery_enabled', false);
    const expressFlowEnabled = useFlag('express_flow_is_enabled', false);

    async function onPresenterClick(presenter: Presenter): Promise<void> {
        if (presenter.id === currentAvatar?.id) {
            return setEditMode(true);
        }

        const disabledSPPlus = presenter.type === VideoType.CLIP && isNonPremiumUser(userPlan) && !user?.isTrial && !isAnonymous;
        if (disabledSPPlus) {
            setShowUpgradeModal(true);
            return;
        }

        if (ttsIsSelectedDefault || presenter.voice_id || presenter.voice || presenter.sentiments?.length) {
            const defaultPresenterVoice = getDefaultVoice(presenter, voices);

            setSelectedVoice(defaultPresenterVoice);
            setSelectedLanguage(defaultPresenterVoice.language);
            setTtsIsSelectedDefault(true);
        }

        addAvatar(store.activePage, presenter);
    }

    const onAvatarUpload = async (avatar: Presenter | undefined) => {
        if (avatar) {
            setSelectedPresenter(avatar.id);
            await addAvatar(store.activePage, avatar);
        }
    };

    const { loading: uploadLoading, onUploadClick } = useUploadAvatar({ from: 'advanced', onUpload: onAvatarUpload });

    const handleModalOpen = (from: string) => () => {
        mixpanel?.track('avatar-create-modal', {
            event: MixPanelEvent.CLICK,
            from,
        });
        openCreateAvatarModal({
            from: pathname,
        });
    };

    const showAvatarBanner =
        !(avatarsLoading || userAvatars.some(({ type, created_by }) => type && type !== VideoType.TALK && created_by === user?.sub)) &&
        avatarsDiscoveryEnabled;

    const uploadInsteadCreate = !expressFlowEnabled || !avatarsDiscoveryEnabled;

    return (
        <>
            <AvatarDiscoverabilityCard
                onAvatarUpload={onAvatarUpload}
                handleModalOpen={handleModalOpen('editor-avatar-banner')}
                show={showAvatarBanner}
            />
            <AvatarTabs filter={filter} setFilter={setFilter} maxWidth="392px" />
            <PresentersContainer>
                <Tooltip
                    disableHoverListener={!uploadInsteadCreate}
                    title="Upload a front-facing image with no face obstructions. Min size 512x512px."
                    placement="right"
                    slotProps={{ tooltip: { sx: { backgroundColor: theme.colors.secondary[900], padding: '8px', fontSize: '14px' } } }}>
                    <UploadTooltipAnchor>
                        <AddAvatarCard
                            onUploadClick={uploadInsteadCreate ? onUploadClick : handleModalOpen('editor-avatar-section')}
                            isLoading={uploadLoading}
                            onAvatarUpload={onAvatarUpload}
                            label={uploadInsteadCreate ? 'Upload Avatar' : 'Create Avatar'}
                        />
                    </UploadTooltipAnchor>
                </Tooltip>
                <PresentersList
                    presenters={presentersToShow}
                    onClick={onPresenterClick}
                    onRemove={setPresenterToDelete}
                    selected={currentAvatar}
                />
            </PresentersContainer>

            <UpgradeModal open={showUpgradeModal} setOpen={setShowUpgradeModal} />
        </>
    );
});
